import { images } from '../images';
import type { Course } from '../../types/content';

const yearRound = ['dubai', 'london'];

export const professionalCourses: Course[] = [
  {
    slug: 'celta-certification',
    name: 'CELTA Certification',
    group: 'professional',
    season: 'year-round',
    keyword: 'celta course',
    metaTitle: 'CELTA Course | Start Your English Teaching Career | ES World',
    metaDescription: 'Take a CELTA course and start a career teaching English. Full-time and part-time options. See dates and sample fees.',
    h1: 'CELTA course to start your teaching career',
    lead: 'Our CELTA course is for people who want to teach English to adults. You learn how to plan lessons, manage a class and teach grammar, vocabulary and skills. You also teach real classes and receive feedback from experienced trainers.',
    cardText: 'Earn a qualification to teach English to adults.',
    audience: 'Adults, 18+',
    image: images.teacher,
    facts: [
      { label: 'Course length', value: '4 weeks full-time' },
      { label: 'Timetable', value: 'Weekdays, full day' },
      { label: 'English level', value: 'Advanced (C1) and up' },
      { label: 'Start dates', value: '00 Month 0000' },
    ],
    valueTitle: 'Teach real classes with confidence',
    valueText: 'You learn a clear method and then use it with real students. Trainers watch your lessons and help you improve each week.',
    outcomes: [
      'Plan clear lessons for different levels',
      'Teach grammar and vocabulary in context',
      'Manage pair work and group work',
      'Give useful feedback to learners',
      'Reflect on your teaching and improve it',
      'Apply for English teaching jobs',
    ],
    whoTitle: 'For new and early-career teachers',
    whoText: 'CELTA suits people with little or no teaching experience. You need a high level of English and a good level of education.',
    whoFor: [
      { title: 'Career changers', text: 'You want a new career in teaching.' },
      { title: 'Graduates', text: 'You want to teach English abroad.' },
      { title: 'New teachers', text: 'You already teach but have no formal training.' },
      { title: 'Fluent speakers', text: 'Your English is at an advanced level and you want to teach it.' },
    ],
    weekTitle: 'A sample training week',
    weekText: 'Each day mixes input sessions, lesson planning and teaching practice. You teach real learners several times each week.',
    week: [
      { day: 'Monday', focus: 'Input session on lesson planning' },
      { day: 'Tuesday', focus: 'Teaching practice and trainer feedback' },
      { day: 'Wednesday', focus: 'Input session on teaching vocabulary' },
      { day: 'Thursday', focus: 'Teaching practice and peer observation' },
      { day: 'Friday', focus: 'Written assignment and tutorial' },
    ],
    faqs: [
      { q: 'How do I apply for CELTA?', a: 'You complete an application form and a short task. Then you have an interview with a trainer.' },
      { q: 'Is CELTA difficult?', a: 'CELTA is intensive. You need time for planning every day. Trainers support you from the first day to the last.' },
    ],
    ctas: {
      heroPrimary: 'Apply for a CELTA place',
      heroSecondary: 'See the training week',
      midPrimary: 'See CELTA fees',
      midLink: { label: 'Compare with Teacher Training', to: '/programmes/teacher-training-course' },
      bottomPrimary: 'Send my CELTA question',
    },
    locations: yearRound,
  },
  {
    slug: 'teacher-training-course',
    name: 'Teacher Training Course',
    group: 'professional',
    season: 'year-round',
    keyword: 'english teacher training course',
    metaTitle: 'English Teacher Training Course | New Methods | ES World',
    metaDescription: 'Join our English teacher training course. Learn new methods and activities you can use in your class. See dates.',
    h1: 'English teacher training course for working teachers',
    lead: 'Our English teacher training course helps working teachers grow their skills. You learn new methods, try new activities and share ideas with teachers from other countries. The course is practical, so you can use new ideas in your next lesson.',
    cardText: 'New methods and activities for working teachers.',
    audience: 'Teachers, 21+',
    image: images.teacher,
    facts: [
      { label: 'Course length', value: '1 to 4 weeks' },
      { label: 'Hours', value: '000 a week' },
      { label: 'English level', value: 'Upper-intermediate (B2) and up' },
      { label: 'Start dates', value: '00 Month 0000' },
    ],
    valueTitle: 'Bring new ideas to your class',
    valueText: 'You learn practical methods and try them in workshops. You leave with a set of activities you can use with your own students.',
    outcomes: [
      'Use new methods for speaking and listening',
      'Plan lessons for mixed-level groups',
      'Use technology in class with a clear purpose',
      'Improve your own English and classroom language',
      'Assess students in a fair and clear way',
      'Share ideas with teachers from other countries',
    ],
    whoTitle: 'For teachers who want to grow',
    whoText: 'This course suits English teachers in schools, universities and language centres. Teachers can also join as a school team.',
    whoFor: [
      { title: 'School teachers', text: 'You teach English at a primary or secondary school.' },
      { title: 'University tutors', text: 'You teach English for academic purposes.' },
      { title: 'Language centre teachers', text: 'You teach adults or teens in small groups.' },
      { title: 'School leaders', text: 'You want to train your teaching team.' },
    ],
    weekTitle: 'Workshops you can use',
    weekText: 'Each day has a theme. You watch a demo lesson, try the method in pairs and plan how to use it in your class.',
    week: [
      { day: 'Monday', focus: 'Speaking activities for all levels' },
      { day: 'Tuesday', focus: 'Teaching grammar through context' },
      { day: 'Wednesday', focus: 'Technology in the classroom' },
      { day: 'Thursday', focus: 'Assessment and feedback' },
      { day: 'Friday', focus: 'Short practice lessons and an action plan' },
    ],
    faqs: [
      { q: 'Can a group of teachers come together?', a: 'Yes. We can plan a course for a school team. Ask us for a group quote.' },
      { q: 'Do I receive a certificate?', a: 'Yes. You receive an ES World certificate of attendance at the end of the course.' },
    ],
    ctas: {
      heroPrimary: 'Plan my training course',
      heroSecondary: 'View the workshop week',
      midPrimary: 'See training fees',
      midLink: { label: 'Compare with CELTA', to: '/programmes/celta-certification' },
      bottomPrimary: 'Send my training enquiry',
    },
    locations: yearRound,
  },
  {
    slug: 'digital-marketing',
    name: 'Digital Marketing',
    group: 'professional',
    season: 'year-round',
    keyword: 'digital marketing course',
    metaTitle: 'Digital Marketing Course in English | Practical Projects | ES World',
    metaDescription: 'Take a digital marketing course in English. Learn social media, search and content with real projects. See dates.',
    h1: 'Digital marketing course with practical projects',
    lead: 'Our digital marketing course teaches the skills that companies use to reach customers online. You learn social media, search, content and email marketing through real projects. All classes are in English, so you also build your professional English.',
    cardText: 'Learn online marketing skills, taught in English.',
    audience: 'Adults, 18+',
    image: images.hero,
    facts: [
      { label: 'Course length', value: '000 weeks' },
      { label: 'Hours', value: '000 a week' },
      { label: 'English level', value: 'Intermediate (B1) and up' },
      { label: 'Start dates', value: '00 Month 0000' },
    ],
    valueTitle: 'Learn skills employers ask for',
    valueText: 'Each module ends with a practical task, such as a social media plan or a search report. You build a portfolio that you can show employers.',
    outcomes: [
      'Plan a social media campaign',
      'Understand how search engines show pages',
      'Write content for websites and emails',
      'Read basic analytics reports',
      'Build a portfolio of real tasks',
      'Present a marketing plan in English',
    ],
    whoTitle: 'For career starters and changers',
    whoText: 'This course suits people who want a job in marketing or want to market their own business. You need an intermediate English level.',
    whoFor: [
      { title: 'Graduates', text: 'You want your first job in marketing.' },
      { title: 'Career changers', text: 'You want to move into a digital role.' },
      { title: 'Business owners', text: 'You want more customers online.' },
      { title: 'Freelancers', text: 'You want to offer marketing services.' },
    ],
    weekTitle: 'Weeks built on projects',
    weekText: 'You learn a new tool or method each week, and then you use it in a project. Trainers review your work and give feedback.',
    week: [
      { day: 'Monday', focus: 'Module input and a case study' },
      { day: 'Tuesday', focus: 'Practice with marketing tools' },
      { day: 'Wednesday', focus: 'Project work in small teams' },
      { day: 'Thursday', focus: 'Professional English for marketing' },
      { day: 'Friday', focus: 'Project presentation and feedback' },
    ],
    faqs: [
      { q: 'Do I need marketing experience?', a: 'No. The course starts with the basics. You only need an intermediate level of English and a laptop.' },
      { q: 'Is there a shorter option?', a: 'Yes. Our Digital Marketing short course covers the basics in a few weeks.' },
    ],
    ctas: {
      heroPrimary: 'Ask about the next intake',
      heroSecondary: 'See a project week',
      midPrimary: 'See marketing course fees',
      midLink: { label: 'Try the short course first', to: '/programmes/digital-marketing-short-course' },
      bottomPrimary: 'Send my marketing enquiry',
    },
    locations: yearRound,
  },
  {
    slug: 'athe-diplomas',
    name: 'ATHE Diplomas',
    group: 'professional',
    season: 'year-round',
    keyword: 'athe diploma',
    metaTitle: 'ATHE Diploma in Business and Management | ES World',
    metaDescription: 'Study an ATHE diploma in business and management, taught in English. Evening classes. See intakes and sample fees.',
    h1: 'ATHE diploma courses in business and management',
    lead: 'Our ATHE diploma courses help you gain a business or management qualification in English. You study modules such as leadership, marketing and finance. Classes use case studies and assignments, so you learn skills that you can use at work.',
    cardText: 'Business and management diplomas, taught in English.',
    audience: 'Adults, 18+',
    image: images.classroom,
    facts: [
      { label: 'Course length', value: '000 months' },
      { label: 'Timetable', value: 'Evening classes' },
      { label: 'English level', value: 'Upper-intermediate (B2) and up' },
      { label: 'Intakes', value: '00 Month 0000' },
    ],
    valueTitle: 'Gain a qualification for your career',
    valueText: 'You complete assignments based on real business problems. Tutors guide you in each module and give feedback on your drafts.',
    outcomes: [
      'Understand key ideas in business and management',
      'Write structured reports and assignments',
      'Analyse real business case studies',
      'Lead discussions and present findings',
      'Plan your next career or study step',
      'Improve your academic and business English',
    ],
    whoTitle: 'For working adults and graduates',
    whoText: 'These diplomas suit people who want a business qualification while they work. You need an upper-intermediate English level.',
    whoFor: [
      { title: 'Working professionals', text: 'You want a qualification and you study after work.' },
      { title: 'Future managers', text: 'You plan to lead a team.' },
      { title: 'Graduates', text: 'You want to add a business qualification.' },
      { title: 'Further study planners', text: 'You plan to continue to a degree.' },
    ],
    weekTitle: 'A sample study week',
    weekText: 'Classes run on set evenings. Between classes, you read, plan and write your assignment with support from your tutor.',
    week: [
      { day: 'Monday', focus: 'Evening class: module input' },
      { day: 'Tuesday', focus: 'Self-study and reading' },
      { day: 'Wednesday', focus: 'Evening class: case study discussion' },
      { day: 'Thursday', focus: 'Assignment writing with tutor support' },
      { day: 'Friday', focus: 'Online tutorial' },
    ],
    faqs: [
      { q: 'What level are the diplomas?', a: 'Different diploma levels are available. Our team will help you choose the right level for your education and goals.' },
      { q: 'Can I work while I study?', a: 'Many students study in the evening and work during the day. Visa rules about work are different in each location.' },
    ],
    ctas: {
      heroPrimary: 'Ask about diploma levels',
      heroSecondary: 'See a study week',
      midPrimary: 'See diploma fees',
      midLink: { label: 'Explore Digital Marketing', to: '/programmes/digital-marketing' },
      bottomPrimary: 'Send my diploma enquiry',
    },
    locations: yearRound,
  },
];
